import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseService } from '../../services/DatabaseService';
import { QRCodeCanvas } from 'qrcode.react';
import ResultsView from './ResultsView';
import LobbyView from './LobbyView';
import ResultsAnimation from './ResultsAnimation';

const PollDetail = () => {
  // State management
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('details'); // 'details', 'lobby', 'results'
  const [copySuccess, setCopySuccess] = useState('');
  
  // Get authentication context, route parameters, and navigation
  const { currentUser } = useAuth();
  const { pollId } = useParams();
  const navigate = useNavigate();
  
  // Generate shareable poll URL
  const pollUrl = `${window.location.origin}/polls/${pollId}/vote`;
  
  // Fetch poll data on component mount
  useEffect(() => {
    const fetchPoll = async () => {
      try {
        const pollData = await DatabaseService.getPoll(pollId);
        setPoll(pollData);
      } catch (err) {
        setError('Failed to load poll: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPoll();
  }, [pollId]);
  
  // Handle poll deletion
  const handleDeletePoll = async () => {
    try {
      setDeleting(true);
      await DatabaseService.deletePoll(pollId, currentUser.uid);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to delete poll: ' + err.message);
      setDeleting(false);
    }
  };
  
  // Copy poll URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(pollUrl)
      .then(() => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };
  
  // Switch to lobby view
  const startLobby = () => {
    setViewMode('lobby');
  };
  
  // Check if current user created this poll
  const isCreator = poll && currentUser && poll.createdBy === currentUser.uid;
  const isPollActive = poll && poll.isActive;
  
  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  
  // Show error message if loading failed
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
        <div className="mt-4">
          <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-900">
            &larr; Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  // Show message if poll not found
  if (!poll) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">Poll not found</span>
        </div>
        <div className="mt-4">
          <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-900">
            &larr; Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  // Show lobby view if in lobby mode
  if (viewMode === 'lobby') {
    return <LobbyView poll={poll} isHost={isCreator} />;
  }
  
  // Main poll detail view
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Poll Header */}
      <div className="border-b border-gray-200 pb-5 mb-5">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{poll.title}</h1>
            {poll.category && (
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {poll.category}
                </span>
              </div>
            )}
          </div>
          
          {/* Show delete button for poll creator */}
          {isCreator && (
            <div className="flex">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="ml-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          )}
        </div>
        
        {poll.description && (
          <p className="mt-2 text-sm text-gray-500">
            {poll.description}
          </p>
        )}
        
        {/* Poll metadata/stats */}
        <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-500">
          <div className="flex items-center">
            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            Created: {new Date(poll.createdAt).toLocaleDateString()}
          </div>
          
          <div className="flex items-center ml-4">
            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            {poll.participantIds ? `${poll.participantIds.length} participants` : '0 participants'}
          </div>
          
          <div className="flex items-center ml-4">
            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Status: {isPollActive ? 'Active' : 'Closed'}
          </div>
          
          <div className="flex items-center ml-4">
            <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" />
            </svg>
            Phase: {poll.status === 'waiting' ? 'Waiting for participants' : 
                   poll.status === 'voting' ? 'Voting in progress' : 
                   'Completed'}
          </div>
        </div>
      </div>
      
      {/* View mode tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setViewMode('details')}
            className={`${
              viewMode === 'details'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
          >
            Details
          </button>
          
          <button
            onClick={() => setViewMode('results')}
            className={`${
              viewMode === 'results'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
          >
            Results
          </button>
        </nav>
      </div>
      
      {/* Content based on view mode */}
      {viewMode === 'details' && (
        <div>
          {/* Poll sharing section */}
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Share this poll</h2>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1 w-full sm:w-auto">
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    readOnly
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                    value={pollUrl}
                  />
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {copySuccess || 'Copy'}
                  </button>
                </div>
              </div>
              
              {/* QR code for sharing */}
              <div className="p-2 bg-white border rounded">
                <QRCodeCanvas value={pollUrl} size={96} />
              </div>
            </div>
          </div>
          
          {/* Poll options list */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Options</h2>
            <ul className="space-y-2">
              {poll.options.map((option, index) => (
                <li key={option.id} className="flex items-center bg-gray-50 p-3 rounded border">
                  <span className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-800 rounded-full font-medium">
                    {index + 1}
                  </span>
                  <span className="ml-3 text-gray-900">{option.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {/* Results view */}
      {viewMode === 'results' && (
        <ResultsView pollId={pollId} />
      )}
      
      {/* Live Mode Button - only shown to poll creator when poll is active */}
      {isPollActive && isCreator && viewMode === 'details' && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={startLobby}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Start Live Poll
          </button>
        </div>
      )}
      
      {/* Status info box for active/completed polls */}
      {poll.status !== 'waiting' && viewMode === 'details' && (
        <div className={`mt-6 p-4 rounded-lg border ${
          poll.status === 'voting' ? 'bg-green-50 border-green-200' : 
          poll.status === 'completed' ? 'bg-blue-50 border-blue-200' : ''
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 rounded-full p-1 ${
              poll.status === 'voting' ? 'bg-green-100' : 
              poll.status === 'completed' ? 'bg-blue-100' : ''
            }`}>
              {poll.status === 'voting' ? (
                <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                poll.status === 'voting' ? 'text-green-800' : 
                poll.status === 'completed' ? 'text-blue-800' : ''
              }`}>
                {poll.status === 'voting' ? 'Voting in progress' : 'Poll completed'}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {poll.status === 'voting' 
                  ? 'This poll is currently active with voting in progress.' 
                  : 'This poll has been completed. You can view the results.'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete poll
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this poll? All data associated with this poll will be permanently removed. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                  onClick={handleDeletePoll}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollDetail;